/* The customer journey — L11, CJ-01…CJ-32 core path, full fidelity.
   Spec: Bible v3.3 §A/§B + WF-01 v5 frames (copy verbatim where drawn).
   Rule 1: nothing conditions the reveal on a review. Rule 2: identity
   before any off-site link. The 120 s clock always reveals.
   ?cadeau=aucun (« Rien — la demande seule »): the player's no-gift path (apps/player/d.html, D3 re-vet 08/10) —
   the entry, the demande, then a merci. No roue, no clock, no address, and no word of a gift on any screen.
   ?variante=suivre (« Suivez-nous »): the player's follow screen, ?reseau= naming the network(s), one or two.
   ?test= (MA-8, FQ-I16): the merchant's test link. The player's band says it counts nowhere, the gift says it validates
   only in mode test and that no e-mail leaves; the scan and the play go to the test read (test-log.ts), nothing else. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { REVEAL_SECONDS, draw, type Gift, type Step } from './machine'
import { getRoue, designFor, wheelStops, THEMES } from '@/lib/wheel'
import { getAsk } from '@/lib/asks'
import { addMessage } from '@/lib/messages'
import { getIdentityFields } from '@/lib/scope'
import { useWallets } from '@/lib/wallet'
import { NO_GIFT_TEXTS, TEXT_DEFAULTS, allCampaigns, editableOf, getCampaign, getTypes, mergeSupports, previewUrl, recipeFromShape, servedBy, shapeOf, visitFlowOf, visitGiftOf, visitKeyOf, type Campaign, type Shape } from '@/lib/campaign'
import { useCampaigns, useLocations, useSupports } from '@/lib/api'
import { currentWorld } from '@/mocks/worlds'
import { recordTest } from './test-log'
import './journey.css'

const roueFor = (id: string | null) => id ? getRoue(id) : undefined
/* I-7 · ?cadeau=<typeId> (not « aucun »): the direct gift's type, revealed without a roue */
const typeFor = (id: string | null) => id && id !== 'aucun' ? getTypes().find(t => t.id === id) ?? null : null
/* the reveal sentence reads « C’est gagné : un café offert. » — the label, lower-cased into the sentence */
const lower = (l: string) => l.charAt(0).toLowerCase() + l.slice(1)
const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
/** what a retour privé keeps of its sender: a first name and an initial from the e-mail's local part, or « Anonyme » — never the address (D-50) */
const fromLabel = (email: string): string => {
  const local = email.trim().split('@')[0] ?? ''
  if (!local) return 'Anonyme'
  const parts = local.split(/[._-]+/).filter(Boolean)
  const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  return parts.length >= 2 ? `${cap(parts[0])} ${parts[1].charAt(0).toUpperCase()}.` : cap(parts[0])
}

/* MA-8 · the mock's test link is /d/{token}?test=t1.<expiry, base 36>.… (handlers-b). Like the player, the demo resolves
   the support at the scan: the journey it opens now, with the test and its campaign carried along. Past its two hours,
   the player's refusal, and nothing runs. */
/** Per-visit ruling P6 · the test link's `visit=N` (1-based): the live campaign reduced to that visit — its resolved gift, order and
    ask — as the player plays it in test mode, whatever the tester's visits before (G1). A Simple, or N out of range, plays as is. */
function visitAs(c: Campaign, n: number): Campaign {
  const live: Campaign = { ...c, pending: undefined }
  const sh = shapeOf(live)
  if (sh.type !== 'parcours' || sh.steps.length < 2 || !Number.isInteger(n) || n < 1 || n > sh.steps.length) return live
  const g = visitGiftOf(live, n - 1); const f = visitFlowOf(live, n - 1)
  const shape: Shape = { type: 'simple', demande: sh.steps[n - 1].demande, steps: [] }
  return { ...live, shape, recipe: recipeFromShape(shape, g.mode === 'roue' ? g.roueId ?? null : null, f.moment, undefined, g.mode === 'cadeau' ? g.typeId : null), flow: f.flow, countdown: f.countdown }
}
/** Point 2 (0037, A1) · the draft's test link opens /d/{testVersionId} (tv-<campaignId>-<rev8>): the content being written,
    never the live one. The demo stands in for the API's frozen test version with the campaign's editable content (the pending
    next version, else the draft itself); the mock's `pending` read counts a play only while that content's rev is the frozen one. */
const TV_RE = /^tv-(.+)-([0-9a-f]{8})$/
export const draftCampaignOfTv = (tv: string): string | null => TV_RE.exec(tv)?.[1] ?? null
function draftAs(c: Campaign, askName: (id: string) => string | undefined): Campaign {
  const e = editableOf(c)
  return { ...c, name: e.name, shape: e.shape, texts: e.texts, textLength: e.textLength, recipe: recipeFromShape(e.shape, e.giftTypeId ? null : e.roueId, e.moment, askName, e.giftTypeId),
    flow: e.flow, countdown: e.countdown, earlyOpen: e.earlyOpen, look: e.look, thanks: e.thanks, pending: undefined }
}
function DraftTestLink({ tv }: { tv: string }) {
  const [params] = useSearchParams()
  const test = params.get('test') ?? ''
  const exp = parseInt(test.split('.')[1] ?? '', 36)
  const own = getCampaign(draftCampaignOfTv(tv))
  if (!(exp * 1000 > Date.now()) || !own) return <TestExpired />
  const draft = draftAs(own, id => getAsk(id)?.name)
  const visit = Number(params.get('visit') ?? '1')
  const n = Number.isInteger(visit) && visit >= 1 ? visit : 1
  const to = previewUrl(visitAs(draft, n), { id: 'e', label: '', etape: 'entry' }, { monde: currentWorld() })
  return <Navigate replace to={`${to}&test=${encodeURIComponent(test)}&cid=${encodeURIComponent(tv)}&visit=${n}`} />
}
function TestExpired() {
  return (
    <div className="jy"><div className="jy-frame">
      <div className="jy-band">VALESSIO <small>Paris 9</small></div>
      <div className="jy-body" style={{ textAlign: 'center', justifyContent: 'center' }} data-testid="jy-test-expire">
        <h1 className="jy-h" style={{ fontSize: 21 }}>Ce lien de test a expiré. Relancez le test depuis votre espace marchand.</h1>
        <p className="jy-sub">Rien n’a été joué ni compté.</p>
      </div>
    </div></div>
  )
}
function TestLink({ token }: { token: string }) {
  const [params] = useSearchParams()
  const { data: supports } = useSupports()
  const { data: campaigns } = useCampaigns()
  const { data: locations } = useLocations()
  const test = params.get('test') ?? ''
  const exp = parseInt(test.split('.')[1] ?? '', 36)
  if (!(exp * 1000 > Date.now())) return <TestExpired />
  if (!supports || !campaigns || !locations) return null
  const s = mergeSupports(supports).find(x => x.token === token)
  const cur = s ? servedBy(s, allCampaigns(campaigns, Object.fromEntries(locations.map(l => [l.id, l.name])))).current : null
  const own = cur ? getCampaign(cur.id) : undefined
  const visit = Number(params.get('visit') ?? '')
  const to = own ? previewUrl(visit >= 1 ? visitAs(own, visit) : own, { id: 'e', label: '', etape: 'entry' }, { monde: currentWorld() }) : `/d/demo?monde=${currentWorld()}&support=${encodeURIComponent(token)}`
  return <Navigate replace to={`${to}&test=${encodeURIComponent(test)}${cur ? `&cid=${encodeURIComponent(cur.id)}` : ''}${own && visit >= 1 ? `&visit=${visit}` : ''}`} />
}

export default function Journey() {
  const { code } = useParams()
  const [params] = useSearchParams()
  return code && code !== 'demo' && params.get('test') ? (draftCampaignOfTv(code) ? <DraftTestLink tv={code} /> : <TestLink token={code} />) : <JourneyScreen />
}

function JourneyScreen() {
  const [params] = useSearchParams()
  const forced = params.get('etape') as Step | null
  const [step, setStep] = useState<Step>(forced ?? 'entry')
  const noGift = params.get('cadeau') === 'aucun'
  /* with no gift, the demande's own steps end on the merci, not on « Votre cadeau est prêt » */
  const done: Step = noGift ? 'merci' : 'ready'
  const [answered, setAnswered] = useState(false) // no gift: an answer or a link ends on the demande's own end message
  const [spinPhase, setSpinPhase] = useState<'idle' | 'spinning' | 'contracting'>('idle')
  const [gift, setGift] = useState<Gift | null>(() => forced && forced !== 'entry' && forced !== 'spin' ? draw(roueFor(params.get('roue')), typeFor(params.get('cadeau'))) : null)
  const [email, setEmail] = useState('')
  const champs = getIdentityFields() // D-69 ① · what the merchant asks for, set once in Établissement
  const wl = useWallets()             // batch 16 · the wallets this device can actually use
  /* D-52 (06/09) · ?ask=<id> — the demande of the campaign (Demandes): its questions, its choices, its prompt, its end message.
     Every demande is skippable (F-10): « Passer » goes to the gift, never to a refusal. */
  const ask = useMemo(() => { const id = params.get('ask'); return id ? getAsk(id) : undefined }, [params])
  const [qi, setQi] = useState(0)
  const [free, setFree] = useState('')
  const [msg, setMsg] = useState('')          // the retour privé being written (batch 12: it lands in the Messages seam)
  const [sent, setSent] = useState(false)
  const quizQs = ask?.kind === 'quiz' && ask.questions?.length ? ask.questions : null
  const [speed, setSpeed] = useState(1) // test harness: 1× / 10× — removed with the mocks
  const [secondsLeft, setSecondsLeft] = useState(REVEAL_SECONDS)
  const deadline = useRef<number | null>(null)
  const reduced = useMemo(() =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches, [])
  /* ruling 04/09 — the LOOK is brand (Identité visuelle), the CONTENT is the roue
     the campaign allocated. ?roue=<id> is how the builder's « Tester le parcours »
     and RW-12's « Voir sur le parcours » open the journey on that exact roue; the
     the mock draw() draws from that roue's types with its odds; the look is Identité's. */
  const roue = useMemo(() => { const id = params.get('roue'); return id ? getRoue(id) : undefined }, [params])
  /* ?theme=<id> — a campaign-level seasonal theme (deep-dive §12); a roue's own style still wins */
  const wheelBg = useMemo(() => {
    const t = THEMES.find(x => x.id === params.get('theme'))
    const d = designFor(roue)
    return `conic-gradient(${wheelStops(t && !roue?.design ? { ...d, a: t.a, b: t.b } : d)})`
  }, [roue, params])

  /* the absolute clock — anchored to a deadline, never to tick counting */
  const clockRunning = !noGift && ['sealed', 'identity', 'invite', 'waiting', 'declined-wait',
    'return-neutral', 'popup-blocked', 'quiz-intro', 'quiz-q', 'question', 'feedback'].includes(step)
  useEffect(() => {
    if (!clockRunning) return
    if (deadline.current === null) deadline.current = Date.now() + REVEAL_SECONDS * 1000 / speed
    const t = window.setInterval(() => {
      const left = Math.max(0, Math.ceil((deadline.current! - Date.now()) * speed / 1000))
      setSecondsLeft(left)
      if (left <= 0) setStep('ready') // the hard deadline always wins (CJ-10)
    }, 250)
    return () => window.clearInterval(t)
  }, [clockRunning, speed])

  /* CJ-08/09 · the observed tab return — the ONE acceleration cue, and it
     conditions on returning (true, observable), never on reviewing (Rule 1).
     Valessio has return-acceleration ON; ?accel=off shows the neutral default. */
  /* ?accel=on|off — the builder's « ouverture anticipée au retour » toggle (D-18); the demo defaults to on */
  const accelOn = params.get('accel') !== 'off'
  const apercu = params.get('apercu') === '1' // D-67 · « Voir la roue » — the merchant's preview: nothing is recorded
  /* MA-8 · a test: the scan when the link opens. The play is the identified participation (contract 05 §5.7b; the API
     sets identified_at at the address): with a gift, at « Recevoir mon cadeau », so a spin alone or a clock that runs
     out records none; with no gift, at the merci. A gift just won is 'issued', as the API answers it. */
  const test = !!params.get('test') && !apercu
  const cid = test ? params.get('cid') : null
  /* v3.3 WF-128 (pack tests-aide) · the link's own expiry (the mock's t1.<expiry, base 36>.…): said on the journey too, under
     the band; a link whose format carries none says nothing */
  const testExp = test ? parseInt((params.get('test') ?? '').split('.')[1] ?? '', 36) : NaN
  const testEnd = Number.isFinite(testExp) && testExp > 0 ? new Date(testExp * 1000).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null
  /* v3.3 WF-134 · the next date of a too-early return, from the establishment's window (?jours=, the merchant's test door);
     the fixture's date otherwise */
  const jours = parseInt(params.get('jours') ?? '', 10)
  const nextDate = Number.isFinite(jours) && jours > 0 ? new Date(Date.now() + jours * 86400e3).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '28 septembre'
  useEffect(() => { if (cid) recordTest(cid, { scan: true }) }, [cid])
  /* P6 (rule 8) · a test of visit N keeps its position, the version played and the visit's content key, so the test read can grey it */
  const visitN = test ? Number(params.get('visit') ?? '') : NaN
  const visitRec = () => {
    /* point 2 · a draft's test (cid = its test version's id): the visit's position, under that id — what `pending.visits[]` reads */
    if (cid && visitN >= 1 && draftCampaignOfTv(cid)) return { position: visitN, version: 0, key: cid }
    const c = cid && visitN >= 1 ? getCampaign(cid) : undefined
    return c ? { position: visitN, version: c.version, key: visitKeyOf({ ...c, pending: undefined }, visitN - 1) } : undefined
  }
  const recordPlay = () => {
    if (cid && gift) recordTest(cid, { play: { at: new Date().toISOString(), gift: { code: gift.code, label: gift.label, status: 'issued', expiresAt: new Date(Date.now() + 30 * 86400e3).toISOString() } } }, visitRec())
  }
  useEffect(() => { if (cid && noGift && step === 'merci') recordTest(cid, { play: { at: new Date().toISOString(), gift: null } }, visitRec()) }, [cid, noGift, step]) // eslint-disable-line react-hooks/exhaustive-deps
  /* The campaign editor's live preview (deep-dive §9): merchant-editable strings arrive
     as query params so the preview shows the words being edited. Locked strings never do. */
  const tx = (k: string, dflt: string) => (params.get(k) || dflt).replace('{établissement}', 'Valessio')
  const plat = params.get('plat') // « Google » or « Google / Pages Jaunes » — the invitation names its platform(s)
  const plat0 = plat ? plat.split(' / ')[0] : null
  /* the player's opensLine: Google opens in Google Maps, any other platform under its name */
  const opensIn = plat0 ? (/google/i.test(plat0) ? 'Google Maps' : plat0) : 'la page d’avis'
  /* « Suivez-nous »: the player's follow screen, at most two networks, the first one primary (`invite` in d.html) */
  const follow = params.get('variante') === 'suivre'
  const nets = (params.get('reseau') ?? '').split(' / ').filter(Boolean).slice(0, 2)
  const followAsk = nets.length === 1 ? `on se retrouve sur ${nets[0]}\u00a0?` : 'on se retrouve sur nos réseaux\u00a0?'
  /* no gift: « Commencer » opens the demande itself — no address first (D3 re-vet), routed like the identity step's variants */
  const askFirst = () => {
    const v = params.get('variante')
    setStep(v === 'quiz' ? 'quiz-intro' : v === 'question' ? 'question' : v === 'message' ? 'feedback' : v === 'rien' ? 'merci' : 'invite')
  }
  const end = () => { setAnswered(true); setStep(done) }
  useEffect(() => {
    if (step !== 'waiting') return
    const onVis = () => {
      if (document.visibilityState === 'visible') setStep(accelOn ? 'return-accel' : 'return-neutral')
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [step, accelOn])

  const startSpin = () => {
    setGift(draw(roue)) // draw resolves server-side before the animation (CJ-05) — over the allocated roue
    if (reduced) { setStep('sealed'); return } // reduced motion: first-class path (F-15)
    setSpinPhase('spinning')
    window.setTimeout(() => setSpinPhase('contracting'), 2400)
    window.setTimeout(() => { setSpinPhase('idle'); setStep('sealed') }, 2800)
  }

  const pct = Math.round(((REVEAL_SECONDS - secondsLeft) / REVEAL_SECONDS) * 100)
  const isReveal = step === 'reveal' || step === 'reveal-simple'
  const g = gift ?? draw(roue)

  return (
    <div className="jy">
      <div className={'jy-frame' + (isReveal ? ' reveal' : '')}>
        <div className="jy-band">VALESSIO <small>Paris 9</small>{apercu && <small style={{ marginLeft: 'auto', opacity: .8 }} data-testid="jy-apercu">aperçu — rien n’est enregistré</small>}</div>
        {/* rider 6 · the band names the visit it plays: « Visite 2 · cadeau test : il ne compte nulle part » */}
        {test && <div className="jy-test" data-testid="jy-test">{visitN >= 1 ? `Visite ${visitN} · ${noGift ? 'test' : 'cadeau test'}\u00a0: il ne compte nulle part` : noGift ? 'Test\u00a0: il ne compte nulle part' : 'Cadeau test\u00a0: il ne compte nulle part'}</div>}
        {test && testEnd && <p className="jy-micro" data-testid="jy-test-fin" style={{ textAlign: 'center', padding: '6px 16px 0' }}>Mode test · le lien expire le {testEnd}</p>}

        {/* ---------- CJ-01 · entry ---------- */}
        {step === 'entry' && (
          <div className="jy-body">
            <div className="jy-merchzone">ZONE VISUELLE MARCHAND</div>
            <h1 className="jy-h">{tx('h', noGift ? NO_GIFT_TEXTS['entry.h'] : 'Un cadeau vous attend chez Valessio.')}</h1>
            <p className="jy-sub">{tx('p', noGift ? NO_GIFT_TEXTS['entry.p'] : 'Scannez, jouez, et retrouvez votre cadeau dans Mes cadeaux — avec ou sans avis.')}</p>
            {!noGift && <p className="jy-micro">1 minute. Aucun téléchargement. Aucun compte pour jouer.</p>}
            <div className="jy-spacer" />
            {/* D-34b (05/09): one gift → a direct reveal, no spin (`?exp=direct`) */}
            {noGift
              ? <button className="jy-btn jy-btn-primary" onClick={askFirst}>Commencer</button>
              : <button className="jy-btn jy-btn-primary" onClick={() => { if (params.get('exp') === 'direct') { setGift(draw(roue, typeFor(params.get('cadeau')))); setStep('sealed') } else setStep('spin') }}>Découvrir mon cadeau</button>}
            {!noGift && <p className="jy-assure">Le cadeau reste disponible, avec ou sans avis.</p>}
          </div>
        )}

        {/* ---------- CJ-05 · wheel ---------- */}
        {step === 'spin' && (
          <div className="jy-body">
            <h1 className="jy-h">À vous de jouer.</h1>
            <p className="jy-sub">Faites tourner la roue pour découvrir votre cadeau.</p>
            <div className="jy-wheelbox">
              <div className={'jy-wheel ' + (spinPhase !== 'idle' ? spinPhase : '')} style={{ background: wheelBg }} />
            </div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={startSpin}
              disabled={spinPhase !== 'idle'}>
              {spinPhase === 'idle' ? 'Faire tourner la roue' : 'La roue tourne…'}
            </button>
            <p className="jy-assure">Chaque part gagne quelque chose.</p>
            {/* v3.3 WF-129 · the assurance under the spin too, in the app's guardrail words */}
            <p className="jy-micro" style={{ textAlign: 'center' }}>Le cadeau reste disponible, avec ou sans avis.</p>
          </div>
        )}

        {/* ---------- CJ-29 · sealed win — the pivot ---------- */}
        {step === 'sealed' && (
          <div className="jy-body">
            <span className="jy-pill">● C’est gagné</span>
            <div className="jy-seal">VOTRE<br />CADEAU</div>
            <h1 className="jy-h" style={{ textAlign: 'center', fontSize: 21 }}>
              Votre cadeau est gagné. Il s’ouvre dans&nbsp;:
            </h1>
            <div className="jy-clock">{fmt(secondsLeft)}</div>
            <div className="jy-bar"><i style={{ width: `${pct}%` }} /></div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('identity')}>Continuer</button>
            <p className="jy-assure">Il s’ouvrira tout seul, quoi que vous fassiez.</p>
          </div>
        )}

        {/* ---------- CJ-02 · identity BEFORE off-site (Rule 2) ---------- */}
        {step === 'identity' && (
          <div className="jy-body">
            <div className="jy-clockchip"><span>✓ Cadeau gagné</span><time>{fmt(secondsLeft)}</time></div>
            <h1 className="jy-h" style={{ fontSize: 21 }}>Où envoyer votre cadeau&nbsp;?</h1>
            <p className="jy-sub">Pour qu’il vous suive, même si vous fermez cette page.</p>
            <button className="jy-provider"><span className="jy-gdot" /> Continuer avec Google</button>
            <div className="jy-divider">ou par e-mail</div>
            {champs.prenom && <input className="jy-in" type="text" autoComplete="given-name" placeholder="Votre prénom" aria-label="Prénom" />}
            <input className="jy-in" type="email" inputMode="email" placeholder="votre@email.fr"
              value={email} onChange={e => setEmail(e.target.value)} />
            {champs.telephone && <input className="jy-in" type="tel" inputMode="tel" autoComplete="tel" placeholder="06 12 34 56 78 (facultatif)" aria-label="Téléphone" />}
            {/* v3.3 WF-130 · the test says so on the identity step itself, in the app's test words */}
            {test && <p className="jy-micro" data-testid="jy-test-identite">C’est un test&nbsp;: aucun e-mail ne part. Données de test uniquement.</p>}
            <button className="jy-btn jy-btn-primary" onClick={() => {
              recordPlay()
              /* ?variante= routes to the other Experience Variants (CJ-15…20);
                 one Stage mixes up to five, the customer meets exactly one */
              const v = params.get('variante')
              setStep(v === 'quiz' ? 'quiz-intro' : v === 'question' ? 'question' : v === 'cadeau-dabord' ? 'gift-first'
                : v === 'message' ? 'feedback' : v === 'rien' ? 'ready' : 'invite')
            }}>Recevoir mon cadeau</button>
            <label className="jy-chkrow">
              <input type="checkbox" defaultChecked style={{ accentColor: 'var(--bmr-action)' }} />
              <span>J’accepte les <u>conditions du jeu</u> de Valessio (v.&nbsp;12).</span>
            </label>
            <div className="jy-spacer" />
            <p className="jy-assure">Un compte gratuit, sans mot de passe. Vos données restent chez Valessio et Build my reviews.</p>
          </div>
        )}

        {/* ---------- CJ-06 · the invitation, during the wait (Rule 1) ---------- */}
        {step === 'invite' && !noGift && !follow && (
          <div className="jy-body">
            <div className="jy-seal sm">CADEAU</div>
            <div className="jy-clockline">Ouverture dans {fmt(secondsLeft)}</div>
            <div className="jy-bar"><i style={{ width: `${pct}%` }} /></div>
            <h1 className="jy-h" style={{ fontSize: 19 }}>{tx('ih', 'En attendant — vous avez aimé votre passage\u00a0?')}</h1>
            <p className="jy-sub">{tx('ip', 'Dites-le en un mot. Ça prend trente secondes et ça compte beaucoup pour l’équipe.')}</p>
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('waiting')}>Laisser un avis{plat0 ? ` sur ${plat0}` : ''}</button>
            <button className="jy-btn jy-btn-quiet" onClick={() => setStep('declined-wait')}>Attendre l’ouverture</button>
            <div className="jy-spacer" />
            <p className="jy-assure">Votre cadeau est déjà gagné. Il s’ouvre à la fin du compte à rebours, avis ou pas.</p>
          </div>
        )}

        {/* ---------- no gift · the avis, then the merci: no seal, no clock, « Passer » (the player's « seule »: its sentence is
             fixed, `invite.p` is not read there) ---------- */}
        {step === 'invite' && noGift && !follow && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 19 }}>Un mot sur votre visite&nbsp;?</h1>
            <p className="jy-sub">Deux lignes suffisent. L’équipe les lit toutes.</p>
            <button className="jy-btn jy-btn-primary" onClick={end}>Laisser un avis{plat0 ? ` sur ${plat0}` : ''}</button>
            <p className="jy-micro" style={{ textAlign: 'center' }}>S’ouvre dans {opensIn}.</p>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-quiet" onClick={() => setStep('merci')}>Passer</button>
          </div>
        )}

        {/* ---------- « Suivez-nous » · the player's follow screen; with a gift, its lead first and the clock on, as the avis ---------- */}
        {step === 'invite' && follow && (
          <div className="jy-body">
            {!noGift && <><div className="jy-seal sm">CADEAU</div>
              <div className="jy-clockline">Ouverture dans {fmt(secondsLeft)}</div>
              <div className="jy-bar"><i style={{ width: `${pct}%` }} /></div></>}
            <h1 className="jy-h" style={{ fontSize: 19 }}>{noGift ? followAsk.charAt(0).toUpperCase() + followAsk.slice(1) : `En attendant, ${followAsk}`}</h1>
            <p className="jy-sub">Les nouveautés et les coulisses, directement dans votre fil.</p>
            {(nets.length ? nets : ['']).map((n, i) => (
              <button key={n || 'net'} className={'jy-btn ' + (i === 0 ? 'jy-btn-primary' : 'jy-btn-quiet')} onClick={noGift ? end : () => setStep('waiting')}>{n ? `Suivre sur ${n}` : 'Suivre'}</button>
            ))}
            {nets.length > 0 && <p className="jy-micro" style={{ textAlign: 'center' }}>S’ouvre dans {nets[0]}.{noGift ? '' : ' Revenez ici ensuite\u00a0: votre cadeau vous attend.'}</p>}
            <div className="jy-spacer" />
            {noGift
              ? <button className="jy-btn jy-btn-quiet" onClick={() => setStep('merci')}>Passer</button>
              : <button className="jy-btn jy-btn-quiet" onClick={() => setStep('declined-wait')}>Attendre l’ouverture</button>}
            {!noGift && <p className="jy-assure">Votre cadeau reste acquis, quoi que vous choisissiez.</p>}
          </div>
        )}

        {/* ---------- CJ-07 · off-site, clock visible ---------- */}
        {step === 'waiting' && (
          <div className="jy-body">
            <div className="jy-seal sm">CADEAU</div>
            <p className="jy-sub" style={{ fontWeight: 600, textAlign: 'center' }}>
              {follow ? 'La page s’est ouverte dans un nouvel onglet.' : 'La page d’avis s’est ouverte dans un nouvel onglet.'}
            </p>
            <div className="jy-clockline">Ouverture dans {fmt(secondsLeft)}</div>
            <div className="jy-bar"><i style={{ width: `${pct}%` }} /></div>
            {/* D-18 · the promise is only made when the merchant turned early opening on — a tab return, never a review */}
            {accelOn && <p className="jy-micro" style={{ textAlign: 'center' }}>Revenez sur cette page pour l’ouvrir plus tôt.</p>}
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-quiet" onClick={() => setStep('reveal')}>Ouvrir mon cadeau maintenant</button>
          </div>
        )}

        {/* ---------- CJ-30 · review declined — warm, no guilt ---------- */}
        {step === 'declined-wait' && (
          <div className="jy-body">
            <div className="jy-seal">CADEAU</div>
            <h1 className="jy-h" style={{ textAlign: 'center', fontSize: 19 }}>Pas de souci.</h1>
            <p className="jy-sub" style={{ textAlign: 'center' }}>Votre cadeau s’ouvre dans un instant.</p>
            <div className="jy-clock">{fmt(secondsLeft)}</div>
            <div className="jy-bar"><i style={{ width: `${pct}%` }} /></div>
            <div className="jy-spacer" />
            {!follow && <p className="jy-assure">Vous pourrez toujours laisser un avis plus tard depuis Mes cadeaux.</p>}
          </div>
        )}

        {/* ---------- CJ-08 · tab return, clock unchanged (accel OFF) ---------- */}
        {step === 'return-neutral' && (
          <div className="jy-body">
            <div className="jy-seal sm">CADEAU</div>
            <h1 className="jy-h" style={{ textAlign: 'center', fontSize: 20 }}>Vous êtes de retour.</h1>
            <div className="jy-clockline">Encore {secondsLeft}&nbsp;s avant l’ouverture</div>
            <div className="jy-bar"><i style={{ width: `${pct}%` }} /></div>
            <div className="jy-spacer" />
            {/* never « Merci pour votre avis » — a tab return proves nothing */}
            <p className="jy-assure">Merci d’avoir pris le temps.</p>
          </div>
        )}

        {/* ---------- CJ-09 · accelerated return — merchant opt-in, Rule 1 intact ---------- */}
        {step === 'return-accel' && (
          <div className="jy-body">
            <div className="jy-seal sm">CADEAU</div>
            <h1 className="jy-h" style={{ textAlign: 'center', fontSize: 21 }}>Vous êtes de retour. On ouvre&nbsp;!</h1>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('reveal')}>Ouvrir mon cadeau</button>
          </div>
        )}

        {/* ---------- CJ-10 · hard deadline — the absolute clock always wins ---------- */}
        {step === 'ready' && (
          <div className="jy-body">
            <div className="jy-seal sm">CADEAU</div>
            <h1 className="jy-h" style={{ textAlign: 'center', fontSize: 21 }}>Votre cadeau est prêt.</h1>
            {sent && <p className="jy-sub" style={{ textAlign: 'center' }}>Votre message est bien parti — merci. L’équipe seule le lira.</p>}
            <div className="jy-clock">0:00</div>
            <div className="jy-bar"><i style={{ width: '100%' }} /></div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('reveal')}>Découvrir mon cadeau</button>
          </div>
        )}

        {/* ---------- CJ-11 · popup blocked — presentation fails, never the gift ---------- */}
        {step === 'popup-blocked' && (
          <div className="jy-body">
            <div className="jy-seal sm">CADEAU</div>
            <h1 className="jy-h" style={{ textAlign: 'center', fontSize: 20 }}>Le lien ne s’est pas ouvert.</h1>
            <p className="jy-sub" style={{ textAlign: 'center' }}>Réessayez, ou attendez simplement l’ouverture.</p>
            <div className="jy-clockline">Ouverture dans {fmt(secondsLeft)}</div>
            <div className="jy-bar"><i style={{ width: `${pct}%` }} /></div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('waiting')}>Réessayer</button>
            <button className="jy-btn jy-btn-quiet" onClick={() => setStep('reveal')}>Ouvrir mon cadeau</button>
          </div>
        )}

        {/* ---------- CJ-15 · variant: gift first, invitation second ---------- */}
        {step === 'gift-first' && (
          <div className="jy-body">
            <div className="jy-giftcard light">
              <span className="name">🎁 5 € offerts sur votre prochaine prestation</span>
              <span className="meta">Déjà dans Mes cadeaux · avant le 27 oct.</span>
            </div>
            <h1 className="jy-h" style={{ fontSize: 19 }}>Vous avez une minute pour partager votre expérience&nbsp;?</h1>
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('waiting')}>Laisser un avis</button>
            <button className="jy-btn jy-btn-quiet" onClick={() => setStep('after')}>Plus tard</button>
            <div className="jy-spacer" />
            <p className="jy-assure">Facultatif — votre cadeau est déjà à vous.</p>
          </div>
        )}

        {/* ---------- CJ-16 · quiz intro — labelled first-party, never a review gate ---------- */}
        {step === 'quiz-intro' && (
          <div className="jy-body">
            {!noGift && <div className="jy-clockchip"><span>✓ Cadeau gagné</span><time>{fmt(secondsLeft)}</time></div>}
            <h1 className="jy-h" style={{ fontSize: 21 }}>{tx('ai', quizQs ? `${ask!.name} — un petit quiz pour Valessio.` : noGift ? NO_GIFT_TEXTS['ask.intro'] : 'Une question express pour Valessio.')}</h1>
            <p className="jy-sub">{quizQs ? `${quizQs.length} question${quizQs.length > 1 ? 's' : ''}. Environ ${quizQs.length * 10} secondes.` : '1 question. Environ 10 secondes.'} {noGift ? 'Vous pouvez passer.' : 'Votre cadeau vous attend juste après — même si vous passez.'}</p>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('quiz-q')}>C’est parti</button>
            <button className="jy-btn jy-btn-quiet" onClick={() => setStep(done)}>Passer</button>
          </div>
        )}

        {/* ---------- CJ-17 · quiz question — single choice, Skip always ---------- */}
        {step === 'quiz-q' && (
          <div className="jy-body">
            <p className="jy-micro" style={{ fontWeight: 700, letterSpacing: '.08em' }}>QUESTION {quizQs ? qi + 1 : 1} SUR {quizQs ? quizQs.length : 1}</p>
            <h1 className="jy-h" style={{ fontSize: 20 }}>{quizQs ? quizQs[qi].text : 'Qu’est-ce qui vous ferait revenir plus souvent\u00a0?'}</h1>
            {(quizQs ? quizQs[qi].choices.filter(c => c.trim()) : ['Des créneaux le soir', 'Un rappel au bon moment', 'Des offres sur les soins', 'Autre chose']).map(o => (
              <button key={o} className="jy-btn jy-btn-quiet" style={{ justifyContent: 'flex-start' }}
                onClick={() => { if (quizQs && qi < quizQs.length - 1) setQi(qi + 1); else if (noGift) end(); else setStep('quiz-done') }}>{o}</button>
            ))}
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-quiet" onClick={() => setStep(done)}>Passer</button>
          </div>
        )}

        {/* ---------- CJ-18 · quiz complete — no spark, spark waits for the reveal ---------- */}
        {step === 'quiz-done' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ textAlign: 'center', fontSize: 21 }}>{ask?.endMessage && ask.kind !== 'retour' ? ask.endMessage : 'Merci, cela aide l’équipe.'}</h1>
            <p className="jy-sub" style={{ textAlign: 'center' }}>Votre cadeau est prêt.</p>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('reveal')}>Découvrir mon cadeau</button>
          </div>
        )}

        {/* ---------- D-52 (06/09) · one question — choices or free text; « Passer » always, the gift never depends on it (F-10) ---------- */}
        {step === 'question' && (
          <div className="jy-body">
            {!noGift && <div className="jy-clockchip"><span>✓ Cadeau gagné</span><time>{fmt(secondsLeft)}</time></div>}
            <p className="jy-micro" style={{ fontWeight: 700, letterSpacing: '.08em' }}>UNE QUESTION</p>
            <h1 className="jy-h" style={{ fontSize: 20 }}>{ask?.kind === 'question' && ask.question ? ask.question : 'Comment nous avez-vous connus\u00a0?'}</h1>
            {ask?.kind === 'question' && ask.answerType === 'libre' ? (<>
              <textarea className="jy-in" rows={3} placeholder="Votre réponse…" value={free} onChange={e => setFree(e.target.value)} style={{ resize: 'none', fontFamily: 'inherit' }} />
              <button className="jy-btn jy-btn-primary" disabled={!free.trim()} onClick={() => noGift ? end() : setStep('quiz-done')}>Envoyer</button>
            </>) : (ask?.kind === 'question' && ask.choices?.length ? ask.choices.filter(c => c.trim()) : ['Une amie', 'Google', 'Instagram', 'En passant devant']).map(o => (
              <button key={o} className="jy-btn jy-btn-quiet" style={{ justifyContent: 'flex-start' }} onClick={() => noGift ? end() : setStep('quiz-done')}>{o}</button>
            ))}
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-quiet" onClick={() => setStep(done)}>{noGift ? 'Passer' : 'Passer et voir mon cadeau'}</button>
            <p className="jy-assure">Vos réponses aident l’équipe. Conservées 24 mois, jamais partagées.</p>
          </div>
        )}

        {/* ---------- CJ-19 · private feedback — all sentiment accepted ---------- */}
        {step === 'feedback' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 21 }}>{ask?.kind === 'retour' && ask.prompt ? ask.prompt : 'Un message pour l’équipe\u00a0?'}</h1>
            <p className="jy-sub">Bonne ou mauvaise expérience — tout nous intéresse. Ce message reste privé, lu par l’équipe seulement.</p>
            <textarea className="jy-in" rows={4} placeholder="Votre message…" value={msg} onChange={e => setMsg(e.target.value)} style={{ resize: 'none', fontFamily: 'inherit' }} />
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" disabled={!msg.trim()} onClick={() => {
              /* the message becomes data the moment it is sent — the merchant reads it in Messages, never anywhere public (D-50);
                 in the real API this is the `ask_completed` event carrying the text (Technical, batch 11); a test's counts nowhere (MA-8) */
              if (!apercu && !test) addMessage({ askId: ask?.id ?? 'a-retour', campaign: params.get('campagne') ?? 'Campagne', text: msg.trim(), from: fromLabel(email) })
              setSent(true); if (noGift) end(); else setStep('ready')
            }}>Envoyer</button>
            <button className="jy-btn jy-btn-quiet" onClick={() => setStep(done)}>{noGift ? 'Passer' : 'Passer et voir mon cadeau'}</button>
          </div>
        )}

        {/* ---------- CJ-12 · the reveal — the one Spark moment ---------- */}
        {step === 'reveal' && (
          <div className="jy-body">
            <div className="jy-spark" />
            <div className="jy-revealcard">
              <h1 className="jy-h" style={{ fontSize: 24 }}>C’est gagné&nbsp;: {lower(g.label)}.</h1>
              <p className="jy-sub" style={{ marginTop: 8 }}>
                À utiliser chez {g.location} avant le {g.validUntil}. Votre cadeau est enregistré dans Mes cadeaux.
              </p>
              <div className="jy-code" style={{ marginTop: 12 }}>{g.code}</div>
              {test && <p className="jy-micro" style={{ marginTop: 10 }}>Cadeau test&nbsp;: il se valide seulement en mode test, et n’est compté nulle part.</p>}
            </div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('marketing')}>Voir mon cadeau</button>
            {!test && wl.map(w => (<button key={w.id} className="jy-btn jy-btn-quiet" data-wallet={w.id}>{w.add}</button>))}
          </div>
        )}

        {/* ---------- CJ-03 · marketing ask — AFTER the reveal, unchecked, skippable ---------- */}
        {step === 'marketing' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 21 }}>Votre cadeau est bien gardé.</h1>
            <div className="jy-giftcard light">
              <span className="name">Envie de rester au courant&nbsp;?</span>
              <span className="meta">Recevoir les offres et actualités de Valessio. Facultatif — sans lien avec votre cadeau.</span>
              <label className="jy-chkrow" style={{ marginTop: 6 }}>
                <input type="checkbox" style={{ accentColor: 'var(--bmr-action)' }} />
                <span>Oui, je veux bien.</span>
              </label>
            </div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('after')}>Continuer</button>
            {!test && <p className="jy-assure">Les messages liés à votre cadeau (envoi, rappel d’expiration) sont toujours inclus.</p>}
          </div>
        )}

        {/* ---------- CJ-13 · e-mail delayed — never hides the gift ---------- */}
        {step === 'email-delayed' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 21 }}>Votre cadeau est bien gardé.</h1>
            <p className="jy-sub">L’e-mail tarde un peu. Votre cadeau est déjà dans Mes cadeaux — rien à faire.</p>
            <div className="jy-giftcard light">
              <span className="name">{g.label}</span>
              <span className="meta">{g.location} · avant le {g.validUntil}</span>
            </div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary">Ouvrir Mes cadeaux</button>
            <button className="jy-btn jy-btn-quiet">Renvoyer l’e-mail</button>
          </div>
        )}

        {/* ---------- CJ-32 · returned after the reveal — restore, no second spark ---------- */}
        {step === 'restored' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 21 }}>Content de vous revoir, Léa.</h1>
            <p className="jy-sub">Votre cadeau vous attendait ici.</p>
            <div className="jy-giftcard light">
              <span className="name">{g.label}</span>
              <span className="meta">{g.location} · avant le {g.validUntil} · ● Actif</span>
            </div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary">Voir mon cadeau</button>
            {wl.map(w => (<button key={w.id} className="jy-btn jy-btn-quiet" data-wallet={w.id}>{w.add}</button>))}
            <p className="jy-assure">Il est aussi dans l’e-mail que nous vous avons envoyé.</p>
          </div>
        )}

        {/* ---------- CJ-14 · after — the return hook, spark is spent ---------- */}
        {step === 'after' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 21 }}>Et maintenant&nbsp;?</h1>
            <div className="jy-giftcard light">
              <span className="name">{g.label}</span>
              <span className="meta">À utiliser avant le {g.validUntil} · ● Actif</span>
            </div>
            <p className="jy-sub">Une nouvelle expérience vous attend à votre prochaine visite.</p>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-quiet">Réserver ma prochaine visite</button>
            {test ? <p className="jy-sub">C’est un test&nbsp;: aucun e-mail ne part.</p> : <p className="jy-assure">Tout est dans votre boîte mail — vous pouvez fermer cette page.</p>}
          </div>
        )}

        {/* ---------- CJ-23 · campaign paused (everything-wrong world) ---------- */}
        {step === 'paused' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 21 }}>Ce jeu est momentanément suspendu.</h1>
            <p className="jy-sub">Revenez un peu plus tard — les cadeaux déjà gagnés restent valables.</p>
            <div className="jy-spacer" />
          </div>
        )}

        {/* ---------- CJ-31 · lapsed before identity (R2 terminal state) ---------- */}
        {step === 'lapsed' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 21 }}>Ce cadeau n’est plus disponible.</h1>
            <p className="jy-sub">Il attendait votre e-mail et le délai de 15 minutes est passé. Rien n’a été perdu de votre côté&nbsp;: il n’a jamais été rattaché à un compte.</p>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => { deadline.current = null; setStep('entry') }}>Rejouer</button>
            <p className="jy-assure">Vous pouvez retenter votre chance si le parcours est toujours ouvert.</p>
          </div>
        )}

        {/* ---------- CJ-04 · returning — Journey ledger resume, identity already held ---------- */}
        {step === 'returning' && (
          <div className="jy-body">
            <h1 className="jy-h">Vous revoilà, Léa.</h1>
            <p className="jy-sub">Étape 2 sur 5 chez Valessio.</p>
            <div className="jy-prog"><i className="done" /><i className="cur" /><i /><i /><i /></div>
            <p className="jy-sub" style={{ marginTop: 4 }}>Aujourd’hui&nbsp;: une question express{noGift ? '' : ', puis votre cadeau'}.</p>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('quiz-intro')}>Continuer mon parcours</button>
            {/* no gift: the player's bare return offers no Mes cadeaux */}
            {!noGift && <button className="jy-btn jy-btn-quiet">Voir mes cadeaux</button>}
          </div>
        )}

        {/* ---------- CJ-20 · gift-only variant — no ask anywhere (Bible §34) ---------- */}
        {step === 'no-request' && (
          <div className="jy-body">
            <h1 className="jy-h">À vous de jouer.</h1>
            <div className="jy-wheelbox">
              <div className={'jy-wheel ' + (spinPhase !== 'idle' ? spinPhase : '')} style={{ background: wheelBg }} />
            </div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={startSpin} disabled={spinPhase !== 'idle'}>
              {spinPhase === 'idle' ? 'Faire tourner la roue' : 'La roue tourne…'}
            </button>
            <p className="jy-assure">Aucune invitation — le jeu et le cadeau, c’est tout.</p>
          </div>
        )}

        {/* ---------- CJ-21 · cooldown value — the existing gift, never a refusal (locked #10) ---------- */}
        {step === 'cooldown' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 20 }}>Vous revoilà, Léa.</h1>
            <p className="jy-sub">Votre prochaine expérience sera disponible le <b>{nextDate}</b>.</p>
            <div className="jy-giftcard light">
              <span className="name">{g.label}</span>
              <span className="meta">Toujours valable · avant le 27 octobre · ● Actif</span>
            </div>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary">Voir mes cadeaux</button>
            <button className="jy-btn jy-btn-quiet">Réserver ma prochaine visite</button>
          </div>
        )}

        {/* ---------- CJ-22 · same-session resume — stored assignment, never a reroll (locked #11) ---------- */}
        {step === 'resume' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 20 }}>Reprenez là où vous vous êtes arrêté.</h1>
            <p className="jy-sub">Votre expérience de tout à l’heure est toujours en cours — même roue, même cadeau.</p>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('spin')}>Reprendre</button>
          </div>
        )}

        {/* ---------- CJ-24 · offline / error — gift-safety reassurance, no blame ---------- */}
        {step === 'offline' && (
          <div className="jy-body">
            <h1 className="jy-h" style={{ fontSize: 20 }}>Quelque chose n’a pas fonctionné.</h1>
            <p className="jy-sub">Vérifiez votre connexion et réessayez. Si un cadeau vous a déjà été attribué, il est en sécurité dans Mes cadeaux.</p>
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('entry')}>Réessayer</button>
            <button className="jy-btn jy-btn-quiet">Ouvrir Mes cadeaux</button>
          </div>
        )}

        {/* ---------- CJ-25 · age / territory gate — year-only, pass/fail, no dead end ---------- */}
        {step === 'age-gate' && (
          <div className="jy-body" style={{ textAlign: 'center', justifyContent: 'center' }}>
            <h1 className="jy-h" style={{ fontSize: 19 }}>Ce jeu est réservé aux 18 ans et plus.</h1>
            <p className="jy-sub">Confirmez votre année de naissance pour continuer.</p>
            <input className="jy-in" defaultValue="19—" inputMode="numeric" aria-label="Année de naissance"
              style={{ textAlign: 'center', maxWidth: 140, margin: '0 auto', font: "500 16px 'IBM Plex Mono', monospace" }} />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('entry')}>Continuer</button>
            <p className="jy-assure">Jeu ouvert en France métropolitaine. <u>Voir les conditions</u>.</p>
          </div>
        )}

        {/* ---------- CJ-26 · e-mail code — 6 digits, resend cooldown, expired auto-recovers ---------- */}
        {step === 'email-code' && (
          <div className="jy-body" style={{ textAlign: 'center', justifyContent: 'center' }}>
            <h1 className="jy-h" style={{ fontSize: 19 }}>Regardez votre boîte mail.</h1>
            <p className="jy-sub">Un code à 6 chiffres est parti à <b>lea@exemple.fr</b>.</p>
            <input className="jy-in" defaultValue="4 8 2 —" inputMode="numeric" aria-label="Code à 6 chiffres"
              style={{ textAlign: 'center', maxWidth: 170, margin: '0 auto', font: "500 20px 'IBM Plex Mono', monospace", letterSpacing: '.3em' }} />
            <p className="jy-micro">Rien reçu&nbsp;? <u>Renvoyer</u> (dans 28 s) · <u>corriger l’adresse</u></p>
            <div className="jy-crit" style={{ textAlign: 'left' }}>✕ Code expiré. Un nouveau code vient d’être envoyé.</div>
          </div>
        )}

        {/* ---------- CJ-27 · hosted-page consent — refusal never breaks the journey (§54.6) ---------- */}
        {step === 'consent' && (
          <div className="jy-body">
            <div className="jy-spacer" />
            <div className="jy-card">
              <p className="jy-sub" style={{ fontWeight: 700, marginBottom: 6 }}>Cookies &amp; mesure</p>
              <p className="jy-sub" style={{ fontSize: 11.5 }}>Valessio souhaite mesurer l’usage de cette page avec ses propres outils. Votre choix ne change rien au jeu ni à votre cadeau.</p>
              <div className="jy-two" style={{ marginTop: 8 }}>
                <button className="jy-btn jy-btn-quiet" onClick={() => setStep('entry')}>Refuser</button>
                <button className="jy-btn jy-btn-primary" onClick={() => setStep('entry')}>Accepter</button>
              </div>
            </div>
          </div>
        )}

        {/* ---------- CJ-28 · non-wheel reveal — same spark rules, no fake spin (§21/§30) ---------- */}
        {step === 'reveal-simple' && (
          <div className="jy-body">
            <div className="jy-spark" />
            <h1 className="jy-h" style={{ fontSize: 21 }}>C’est pour vous&nbsp;: {lower(g.label)}.</h1>
            <p className="jy-sub">Sans roue aujourd’hui — juste une attention de l’équipe. Valable jusqu’au 27 octobre.</p>
            <div className="jy-code" style={{ marginTop: 12 }}>{g.code}</div>
            {test && <p className="jy-micro">Cadeau test&nbsp;: il se valide seulement en mode test, et n’est compté nulle part.</p>}
            <div className="jy-spacer" />
            <button className="jy-btn jy-btn-primary" onClick={() => setStep('marketing')}>Voir mon cadeau</button>
          </div>
        )}

        {/* ---------- no gift · the merci — after an answer or a link, the demande's end message, else the merchant's « phrase de
             remerciement » (?th=), as the player's thanksLine (a retour privé has no end message of its own); after « Passer »,
             « Merci ! ». Never a promise ---------- */}
        {step === 'merci' && (
          <div className="jy-body" style={{ textAlign: 'center', justifyContent: 'center' }}>
            <h1 className="jy-h" style={{ fontSize: 21 }}>{(answered && ((ask?.kind !== 'retour' && ask?.endMessage) || params.get('th'))) || 'Merci\u00a0!'}</h1>
            <p className="jy-sub">{tx('tp', TEXT_DEFAULTS['thanks.p'].value)}</p>
            <div className="jy-spacer" />
            <p className="jy-assure">Vous pouvez fermer cette page.</p>
          </div>
        )}

        {step === 'entry' && (
          <div className="jy-foot">Un parcours créé par Valessio avec <b>buildmyreviews</b> · Conditions · Confidentialité</div>
        )}
      </div>

      {/* test harness — removed when the seam connects the real Worker */}
      <div className="jy-dev" aria-label="Vitesse de test">
        <button className={speed === 1 ? 'on' : ''} onClick={() => setSpeed(1)}>1×</button>
        <button className={speed === 10 ? 'on' : ''} onClick={() => setSpeed(10)}>10×</button>
      </div>
    </div>
  )
}
